// PartnerCommissions — per-partner detail page. Shows:
//   • Pause pill + toggle (super admin)
//   • Historical commission chart (all periods)
//   • Lifetime stat cards
//   • Deal-level list (per-domain rows) with per-deal override + payable
//     toggle (super admin only)
//   • Falls back to monthly summary rows if no deal-level data uploaded.

import { Fragment, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, DollarSign, Pause, Play,
  Pencil, Check, X as XIcon, Loader2, ChevronDown, ChevronRight, Search,
  Mail, ShieldCheck, ArrowRightLeft, Undo2,
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer,
} from "recharts";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { PartnerEditDialog } from "@/components/PartnerEditDialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { usePageBreadcrumb } from "@/context/BreadcrumbContext";
import { DealLinesPanel } from "@/components/commissions/DealLinesPanel";
import { NewPill } from "@/components/commissions/NewPill";
import { PaymentDialog } from "@/components/commissions/PaymentDialog";
import { InquiriesPanel } from "@/components/commissions/InquiriesPanel";
import { ReissueDialog } from "@/components/commissions/ReissueDialog";
import { ProductMixChart } from "@/components/commissions/ProductMixChart";
import { PartnerRangeStats } from "@/components/commissions/PartnerRangeStats";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Stax exports HTML-encode apostrophes (`Chant&#039;s`) and ampersands.
// Decode before rendering so React doesn't show the literal entity.
const decodeHtml = (s) => (s || "")
  .replace(/&#(\d+);/g,  (_, n) => String.fromCharCode(parseInt(n, 10)))
  .replace(/&#x([0-9a-f]+);/gi, (_, h) => String.fromCharCode(parseInt(h, 16)))
  .replace(/&amp;/g,  "&")
  .replace(/&quot;/g, "\"")
  .replace(/&apos;/g, "'")
  .replace(/&lt;/g,   "<")
  .replace(/&gt;/g,   ">");

export default function PartnerCommissions() {
  const { key } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, has } = useAuth();
  const isSuperAdmin = (user?.role || "").toLowerCase() === "super_admin";
  const isStaff = isSuperAdmin || (user?.base_role || "").toLowerCase() === "staff";
  // Each page button has its own permission, independent of commissions.*.
  const can = (name) => isSuperAdmin || has(`partner_commissions_${name}.view`);
  const showActions = can("override");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingPause, setSavingPause] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingDeal, setEditingDeal] = useState(null);
  const [expandedDeal, setExpandedDeal] = useState(null);
  const [savingDeal, setSavingDeal] = useState(false);
  // Periods start COLLAPSED — user opts in to expand each one. State resets
  // to empty on every mount, so re-navigating to this page starts fresh.
  const [expanded, setExpanded] = useState({});   // { period_label: true }
  const [dealSearch, setDealSearch] = useState("");
  const toggleCollapsed = (p) => setExpanded((e) => ({ ...e, [p]: !e[p] }));

  // After an edit we refresh quietly in place — no spinner, no scroll jump.
  const load = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const { data: d } = await api.get(`/commissions/partners/${encodeURIComponent(key)}/detail`);
      setData(d);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load partner commissions");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [key]);

  usePageBreadcrumb(useMemo(() => (data ? [
    { label: "Commissions", to: "/commissions" },
    { label: data?.org?.name || key },
  ] : null), [data, key]));

  const togglePause = async () => {
    if (!data?.org?.org_id) return;
    setSavingPause(true);
    try {
      const next = !data.org.commissions_paused;
      await api.patch(`/organizations/${data.org.org_id}/commissions-pause`, { paused: next });
      toast.success(next ? "Partner paused" : "Partner resumed");
      await load({ silent: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to update pause state");
    } finally { setSavingPause(false); }
  };

  const saveDeal = async (deal, patch) => {
    setSavingDeal(true);
    try {
      await api.patch(`/commissions/deals/${deal.deal_id}`, patch);
      toast.success("Deal updated");
      setEditingDeal(null);
      await load({ silent: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to update deal");
    } finally { setSavingDeal(false); }
  };

  const togglePayable = async (deal) => {
    const next = !deal.payable;
    const flip = (v) => setData((d) => d && ({ ...d, deals: (d.deals || []).map((x) => (x.deal_id === deal.deal_id ? { ...x, payable: v } : x)) }));
    flip(next);  // switch moves instantly; totals refresh from the server
    try {
      await api.patch(`/commissions/deals/${deal.deal_id}`, { payable: next });
      toast.success(next ? "Marked payable" : "Put on hold");
      await load({ silent: true });
    } catch (e) {
      flip(!next);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to update deal");
    }
  };

  const [paymentDialog, setPaymentDialog] = useState(null);  // { period, editing?: payment }
  const [savingPayment, setSavingPayment] = useState(false);
  const submitPayment = async (form) => {
    setSavingPayment(true);
    try {
      const body = {
        period_label: form.period_label,
        amount:       Number(form.amount || 0),
        method:       form.method || "",
        reference:    form.reference || "",
        paid_at:      form.paid_at || null,
        notes:        form.notes || "",
      };
      if (form.payment_id) {
        await api.patch(
          `/commissions/partners/${encodeURIComponent(key)}/payments/${form.payment_id}`,
          body,
        );
        toast.success("Payment updated");
      } else {
        await api.post(
          `/commissions/partners/${encodeURIComponent(key)}/payments`,
          body,
        );
        toast.success("Payment recorded");
      }
      setPaymentDialog(null);
      await load({ silent: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save payment");
    } finally { setSavingPayment(false); }
  };
  const deletePayment = async (payment) => {
    if (!window.confirm(`Delete payment of $${payment.amount}? This cannot be undone.`)) return;
    try {
      await api.delete(`/commissions/partners/${encodeURIComponent(key)}/payments/${payment.payment_id}`);
      toast.success("Payment deleted");
      await load({ silent: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to delete");
    }
  };

  const payments = data?.payments || [];
  const paymentsByPeriod = useMemo(() => {
    const g = {};
    for (const p of payments) {
      const pl = p.period_label || "";
      (g[pl] = g[pl] || []).push(p);
    }
    return g;
  }, [payments]);

  // --- Pro-forma email dialog + accept flow ------------------------------
  const [emailDialog, setEmailDialog] = useState(null);   // { period }
  const [reissue, setReissue] = useState(null);           // { period }  — Iter 306
  const focusInquiry = searchParams.get("inquiry") || "";
  const [sendingEmail, setSendingEmail] = useState(false);
  const sendProformaEmail = async (form) => {
    setSendingEmail(true);
    try {
      const body = {
        to:        (form.to   || "").split(",").map((s) => s.trim()).filter(Boolean),
        cc:        (form.cc   || "").split(",").map((s) => s.trim()).filter(Boolean),
        note:      form.note  || "",
        test_send: !!form.test_send,
        itemised:  !!form.itemised,
      };
      const { data: r } = await api.post(
        `/commissions/partners/${encodeURIComponent(key)}/periods/${encodeURIComponent(form.period)}/email`,
        body,
      );
      if (r.test_send) {
        toast.success(`Test email sent to ${(r.to || []).join(", ")} — check your inbox`);
      } else if (r.sandbox) {
        toast.success(`[Sandbox] Logged — would have gone to ${(r.to || []).join(", ")}`);
      } else {
        toast.success(`Sent to ${(r.to || []).join(", ")}`);
      }
      setEmailDialog(null);
      await load({ silent: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to send email");
    } finally { setSendingEmail(false); }
  };

  const acceptances = data?.acceptances || {};
  const lastEmails  = data?.last_emails || {};
  const [acceptingPeriod, setAcceptingPeriod] = useState("");
  const acceptProforma = async (period) => {
    if (!window.confirm(`Accept pro-forma for ${period}? Amount changes will be locked (payments can still be recorded).`)) return;
    setAcceptingPeriod(period);
    try {
      await api.post(
        `/commissions/partners/${encodeURIComponent(key)}/periods/${encodeURIComponent(period)}/accept`,
        {},
      );
      toast.success(`Accepted ${period}`);
      await load({ silent: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to accept");
    } finally { setAcceptingPeriod(""); }
  };
  const revokeAcceptance = async (period) => {
    if (!window.confirm(`Revoke acceptance for ${period}? This unlocks amount changes.`)) return;
    setAcceptingPeriod(period);
    try {
      await api.delete(
        `/commissions/partners/${encodeURIComponent(key)}/periods/${encodeURIComponent(period)}/accept`,
      );
      toast.success(`Revoked ${period} acceptance`);
      await load({ silent: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to revoke");
    } finally { setAcceptingPeriod(""); }
  };

  // --- Balance-forward reconciliation ------------------------------------
  const balanceForwardPayments = useMemo(
    () => payments.filter((p) => p.period_label === "balance_forward"),
    [payments],
  );
  const [reconcileDialog, setReconcileDialog] = useState(null);   // { payment }
  const [reconciling, setReconciling] = useState(false);
  const runReconcile = async (form) => {
    setReconciling(true);
    try {
      await api.post(
        `/commissions/partners/${encodeURIComponent(key)}/reconcile-balance`,
        { payment_id: form.payment_id, target_period: form.target_period, note: form.note || "" },
      );
      toast.success(`Applied to ${form.target_period}`);
      setReconcileDialog(null);
      await load({ silent: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to reconcile");
    } finally { setReconciling(false); }
  };

  // Group deals by period and provide a bulk-toggle for every period at once.
  const dealsByPeriod = useMemo(() => {
    const q = dealSearch.trim().toLowerCase();
    const filter = (d) => !q
      || (d.domain    || "").toLowerCase().includes(q)
      || (d.deal_name || "").toLowerCase().includes(q);
    const groups = new Map();
    for (const d of (data?.deals || [])) {
      if (!filter(d)) continue;
      const p = d.period_label || "";
      if (!groups.has(p)) groups.set(p, []);
      groups.get(p).push(d);
    }
    // newest → oldest so recent activity is on top
    return Array.from(groups.entries())
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([period, ds]) => ({
        period,
        deals:            ds,
        commission:       ds.reduce((s, d) => s + ((d.override?.commission ?? d.commission) || 0), 0),
        payable_count:    ds.filter((d) => d.payable).length,
        held_count:       ds.filter((d) => !d.payable).length,
      }));
  }, [data, dealSearch]);

  const bulkTogglePeriod = async (period, payable) => {
    setSavingDeal(true);
    try {
      await api.patch(
        `/commissions/partners/${encodeURIComponent(key)}/periods/${encodeURIComponent(period)}/payable`,
        { payable },
      );
      toast.success(`${payable ? "Released" : "Held"} all deals for ${period}`);
      await load({ silent: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to bulk update payable");
    } finally { setSavingDeal(false); }
  };

  // Broader bulk toggle — flip payable across all deals for this partner,
  // optionally narrowed by level. Scope combos supported:
  //   partner-wide          (no filter)
  //   partner + level       (Bronze/Silver/Gold only)
  //   partner + period      (handled by `bulkTogglePeriod` above)
  //   partner + period + level (via `period` arg)
  const bulkTogglePartner = async ({ level = null, period = null } = {}, payable) => {
    setSavingDeal(true);
    try {
      const body = { payable };
      if (level)  body.level        = level;
      if (period) body.period_label = period;
      await api.patch(
        `/commissions/partners/${encodeURIComponent(key)}/payable`,
        body,
      );
      const scope = [level && `${level}`, period && `${period}`].filter(Boolean).join(" · ") || "all deals";
      toast.success(`${payable ? "Released" : "Held"} ${scope}`);
      await load({ silent: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to bulk update payable");
    } finally { setSavingDeal(false); }
  };

  // Levels present in this partner's deal set (for the level bulk toggles).
  const levelsPresent = useMemo(() => {
    const s = new Set();
    for (const d of (data?.deals || [])) {
      const lv = (d.override?.level ?? d.level_name) || "";
      if (lv) s.add(lv);
    }
    return Array.from(s).sort();
  }, [data]);

  const timeseries = data?.timeseries || [];
  const deals      = data?.deals      || [];
  const rows       = data?.rows       || [];

  // Chart data — commission over time
  const chart = useMemo(() => timeseries.map((t) => ({
    period:     t.period_label,
    commission: t.commission,
    payable:    t.payable_commission,
    revenue:    t.revenue,
  })), [timeseries]);

  if (loading) {
    return (
      <div className="mx-auto max-w-[1400px] px-4 py-6" data-testid="partner-commissions-loading">
        <Loader2 className="mx-auto h-6 w-6 animate-spin text-slate-300 mt-20" />
      </div>
    );
  }

  const paused    = !!data?.org?.commissions_paused;
  const partnerNm = data?.org?.name || key;

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-6" data-testid="partner-commissions-page">
      <div className="flex items-center gap-2 mb-3">
        <Button variant="ghost" size="sm" onClick={() => navigate("/commissions")}
                data-testid="partner-commissions-back" className="h-8 -ml-2">
          <ArrowLeft className="h-4 w-4" /> Back to Commissions
        </Button>
      </div>
      <PageHeader testid="partner-commissions-header" title={partnerNm} icon={DollarSign}
        subtitle={`Historical commissions${data?.org?.partner_type ? ` — ${data.org.partner_type}` : ""}`}>
        {data?.org?.org_id && (
          <div className="flex items-center gap-3">
            <span data-testid="partner-commissions-status-pill"
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium
                              ${paused ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
              {paused ? <><Pause className="h-3 w-3" /> Paused</> : <><Play className="h-3 w-3" /> Active</>}
            </span>
            {can("pause") && (
              <div className="flex items-center gap-2 rounded-md border border-slate-200 px-2 py-1">
                <span className="text-[11px] text-slate-500">Pause partner</span>
                <Switch checked={paused} onCheckedChange={togglePause} disabled={savingPause}
                        data-testid="partner-pause-switch" />
              </div>
            )}
            {isSuperAdmin && (
              <Button variant="outline" size="sm" className="h-9" onClick={() => setEditOpen(true)}
                      data-testid="partner-edit-btn">
                <Pencil className="h-4 w-4" /> Edit partner
              </Button>
            )}
          </div>
        )}
      </PageHeader>

      {paused && (
        <Card className="border-amber-200 bg-amber-50 text-amber-800 shadow-sm p-3 mb-4 flex items-start gap-2"
              data-testid="partner-paused-banner">
          <Pause className="h-4 w-4 mt-0.5" />
          <div className="text-sm">
            <div className="font-medium">Commissions are paused for this partner.</div>
            <div className="text-xs text-amber-700 mt-0.5">
              Amounts still calculate on import, but individual deals are <b>not payable</b> until a super admin
              flips each deal&rsquo;s <b>Payable</b> toggle below.
            </div>
          </div>
        </Card>
      )}

      {can("reconcile") && balanceForwardPayments.length > 0 && (
        <Card className="border-sky-200 bg-sky-50 text-sky-900 shadow-sm p-3 mb-4"
              data-testid="partner-balance-forward-banner">
          <div className="flex items-start gap-2">
            <ArrowRightLeft className="h-4 w-4 mt-0.5" />
            <div className="flex-1 text-sm">
              <div className="font-medium">
                {balanceForwardPayments.length} balance-forward payment{balanceForwardPayments.length === 1 ? "" : "s"} awaiting reconciliation
              </div>
              <div className="text-xs text-sky-800 mt-0.5">
                Apply each historical payment to a real monthly period so the payable owing on that period zeroes out.
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid="balance-forward-chips">
                {balanceForwardPayments.map((p) => (
                  <button key={p.payment_id}
                          onClick={() => setReconcileDialog({ payment: p })}
                          className="inline-flex items-center gap-1.5 rounded border border-sky-300 bg-white px-2 py-1 text-xs hover:bg-sky-100"
                          data-testid={`reconcile-open-${p.payment_id}`}>
                    <span className="font-medium tabular-nums text-sky-900">{money(p.amount)}</span>
                    {p.method && <span className="text-slate-500">· {p.method}</span>}
                    {p.reference && <span className="text-slate-400">· {p.reference}</span>}
                    {p.paid_at && <span className="text-slate-400">· {(p.paid_at || "").slice(0, 10)}</span>}
                    <ArrowRightLeft className="h-3 w-3 text-sky-500" />
                    <span className="text-sky-700 font-medium">Apply</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </Card>
      )}

      <PartnerRangeStats timeseries={timeseries} deals={deals} paused={paused} />
      {isStaff && <InquiriesPanel partnerKey={key} focusId={focusInquiry} onReissued={() => load({ silent: true })} />}

      {/* Chart */}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2 mb-0">
      <Card className="border-slate-200 shadow-sm p-3 mb-4">
        <div className="text-[10px] uppercase tracking-wider font-medium text-slate-500 mb-2">
          Commission by period
        </div>
        <div className="w-full h-[240px]" data-testid="partner-commissions-chart">
          {chart.length === 0 ? (
            <div className="flex h-full items-center justify-center text-sm text-slate-400">
              No historical periods yet.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chart} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="period" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8"
                       tickFormatter={(v) => `$${v.toLocaleString()}`} width={70} />
                <Tooltip formatter={(v) => money(v)} />
                <Line type="monotone" dataKey="commission" name="Commission"
                      stroke="#059669" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="payable" name="Payable"
                      stroke="#0ea5e9" strokeWidth={2} strokeDasharray="4 3" dot={{ r: 2 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </Card>
      <ProductMixChart partnerKey={key} months={12} />
      </div>

      {/* Deal-level list (falls back to monthly rows) */}
      <Card className="border-slate-200 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100 bg-slate-50/50">
          <div className="text-[10px] uppercase tracking-wider font-medium text-slate-500">
            {deals.length > 0 ? `Deals (${deals.length})` : `Monthly summary rows (${rows.length})`}
          </div>
          {(can("override") || can("release_hold")) && deals.length > 0 && (
            <span className="text-[10px] text-slate-400">
              Click <Pencil className="inline h-3 w-3" /> to override values or toggle Payable.
            </span>
          )}
        </div>

        {can("release_hold") && deals.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-slate-100 bg-white"
               data-testid="partner-commissions-bulk-bar">
            <span className="text-[10px] uppercase tracking-wider font-medium text-slate-400 mr-1">Bulk actions</span>
            <span className="text-[11px] text-slate-500">Partner-wide:</span>
            <Button variant="outline" size="sm" className="h-7 text-xs"
                    disabled={savingDeal}
                    onClick={() => bulkTogglePartner({}, true)}
                    data-testid="partner-release-all">
              Release all
            </Button>
            <Button variant="outline" size="sm" className="h-7 text-xs text-amber-700 border-amber-200 hover:bg-amber-50"
                    disabled={savingDeal}
                    onClick={() => bulkTogglePartner({}, false)}
                    data-testid="partner-hold-all">
              Hold all
            </Button>
            {levelsPresent.length > 0 && (
              <div className="ml-3 flex items-center gap-1.5 border-l border-slate-200 pl-3">
                <span className="text-[11px] text-slate-500">By level:</span>
                {levelsPresent.map((lv) => (
                  <div key={lv} className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-1.5 py-0.5">
                    <span className="text-[11px] font-medium text-slate-600">{lv}</span>
                    <button onClick={() => bulkTogglePartner({ level: lv }, true)} disabled={savingDeal}
                            title={`Release all ${lv}`}
                            data-testid={`level-release-${lv}`}
                            className="text-emerald-600 hover:text-emerald-800 disabled:opacity-40 text-[10px] px-1">
                      ✓ release
                    </button>
                    <button onClick={() => bulkTogglePartner({ level: lv }, false)} disabled={savingDeal}
                            title={`Hold all ${lv}`}
                            data-testid={`level-hold-${lv}`}
                            className="text-amber-600 hover:text-amber-800 disabled:opacity-40 text-[10px] px-1">
                      hold
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {deals.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-slate-100 bg-slate-50/40">
            <div className="relative flex-1 min-w-[220px] max-w-[360px]">
              <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-slate-400" />
              <Input value={dealSearch} onChange={(e) => setDealSearch(e.target.value)}
                     placeholder="Search domain or customer…"
                     className="pl-7 h-8 text-xs" data-testid="partner-deal-search" />
            </div>
            <div className="flex items-center gap-1.5 ml-auto">
              <Button variant="ghost" size="sm" className="h-7 text-xs text-slate-500"
                      onClick={() => {
                        const next = {};
                        for (const g of dealsByPeriod) next[g.period] = true;
                        setExpanded(next);
                      }}
                      data-testid="partner-expand-all-periods">
                Expand all
              </Button>
              <Button variant="ghost" size="sm" className="h-7 text-xs text-slate-500"
                      onClick={() => setExpanded({})}
                      data-testid="partner-collapse-all-periods">
                Collapse all
              </Button>
            </div>
          </div>
        )}
        <div className="max-h-[calc(100vh-180px)] overflow-auto" data-testid="partner-commissions-scroll">
          <table className="w-full text-sm" data-testid="partner-commissions-table">
            <thead className="sticky top-0 z-10 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 shadow-[0_1px_0_0_rgb(226,232,240)]">
              {deals.length > 0 ? (
                <tr>
                  <th className="px-3 py-2 text-left">Period</th>
                  <th className="px-3 py-2 text-left">Domain</th>
                  <th className="px-3 py-2 text-left">Customer</th>
                  <th className="px-3 py-2 text-left">Level</th>
                  <th className="px-3 py-2 text-right">Revenue</th>
                  <th className="px-3 py-2 text-right">Commission</th>
                  <th className="px-3 py-2 text-center">Payable</th>
                  {showActions && <th className="px-3 py-2 text-right w-24">Actions</th>}
                </tr>
              ) : (
                <tr>
                  <th className="px-3 py-2 text-left">Period</th>
                  <th className="px-3 py-2 text-left">Level</th>
                  <th className="px-3 py-2 text-right">Deals</th>
                  <th className="px-3 py-2 text-right">Revenue</th>
                  <th className="px-3 py-2 text-right">Commission</th>
                  <th className="px-3 py-2 text-right">Rate</th>
                </tr>
              )}
            </thead>
            <tbody className="divide-y divide-slate-100">
              {deals.length > 0 ? dealsByPeriod.flatMap((grp) => {
                const allPayable = grp.held_count === 0;
                const allHeld    = grp.payable_count === 0;
                const colSpan = showActions ? 8 : 7;
                const isCollapsed = !expanded[grp.period];
                const grpPayments = paymentsByPeriod[grp.period] || [];
                const grpPaid     = grpPayments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
                const acceptance  = acceptances[grp.period];
                const lastEmail   = lastEmails[grp.period];
                const header = (
                  <tr key={`period-${grp.period}`} className="bg-slate-100/70 cursor-pointer hover:bg-slate-200/60"
                      data-testid={`period-row-${grp.period}`}
                      onClick={() => toggleCollapsed(grp.period)}>
                    <td colSpan={colSpan} className="px-3 py-1.5">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-xs">
                          {isCollapsed
                            ? <ChevronRight className="h-3.5 w-3.5 text-slate-500" data-testid={`period-collapse-${grp.period}`} />
                            : <ChevronDown  className="h-3.5 w-3.5 text-slate-500" data-testid={`period-expand-${grp.period}`} />}
                          <span className="font-semibold text-slate-700">{grp.period}</span>
                          <span className="text-slate-500">{grp.deals.length} deals · {money(grp.commission)}</span>
                          {allHeld ? (
                            <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">All held</span>
                          ) : allPayable ? (
                            <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700">All payable</span>
                          ) : (
                            <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-medium text-slate-700">
                              {grp.payable_count} payable · {grp.held_count} held
                            </span>
                          )}
                          {grpPaid > 0 && (
                            <span className="rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-medium text-sky-700"
                                  data-testid={`period-paid-pill-${grp.period}`}
                                  title={`${grpPayments.length} payment${grpPayments.length === 1 ? "" : "s"} recorded`}>
                              Paid {money(grpPaid)}
                            </span>
                          )}
                          {acceptance && (
                            <span className="inline-flex items-center gap-1 rounded bg-violet-100 px-1.5 py-0.5 text-[10px] font-medium text-violet-700"
                                  title={`Accepted ${(acceptance.accepted_at || "").slice(0,10)} by ${acceptance.accepted_by_name || "—"}`}
                                  data-testid={`period-accepted-pill-${grp.period}`}>
                              <ShieldCheck className="h-3 w-3" /> Accepted {money(acceptance.amount)}
                            </span>
                          )}
                          {lastEmail && (
                            <span className="inline-flex items-center gap-1 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500"
                                  title={`Last emailed ${(lastEmail.sent_at || "").slice(0,10)} to ${(lastEmail.to || []).join(", ")}`}
                                  data-testid={`period-emailed-pill-${grp.period}`}>
                              <Mail className="h-3 w-3" /> Sent
                            </span>
                          )}
                        </div>
                        {isStaff && (
                          <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                            {can("release_hold") && <>
                            <Button variant="outline" size="sm" className="h-7 text-xs"
                                    disabled={savingDeal || allPayable || !!acceptance}
                                    onClick={() => bulkTogglePeriod(grp.period, true)}
                                    title={acceptance ? "Locked — revoke acceptance first" : undefined}
                                    data-testid={`period-release-all-${grp.period}`}>
                              Release all
                            </Button>
                            <Button variant="outline" size="sm" className="h-7 text-xs text-amber-700 border-amber-200 hover:bg-amber-50"
                                    disabled={savingDeal || allHeld || !!acceptance}
                                    onClick={() => bulkTogglePeriod(grp.period, false)}
                                    title={acceptance ? "Locked — revoke acceptance first" : undefined}
                                    data-testid={`period-hold-all-${grp.period}`}>
                              Hold all
                            </Button>
                            </>}
                            {can("payments") && (
                            <Button variant="outline" size="sm" className="h-7 text-xs text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                                    onClick={() => {
                                      const payable = grp.deals
                                        .filter((d) => d.payable)
                                        .reduce((s, d) => s + ((d.override?.commission ?? d.commission) || 0), 0);
                                      const net = Math.max(0, payable - grpPaid);
                                      setPaymentDialog({
                                        period: grp.period,
                                        suggestedAmount: net.toFixed(2),
                                      });
                                    }}
                                    data-testid={`period-record-payment-${grp.period}`}>
                              Record payment
                            </Button>
                            )}
                            {can("proforma_pdf") && (
                            <Button variant="outline" size="sm" className="h-7 text-xs"
                                    onClick={() => window.open(
                                      `${api.defaults.baseURL}/commissions/partners/${encodeURIComponent(key)}/proforma.pdf?period_label=${grp.period}`,
                                      "_blank"
                                    )}
                                    data-testid={`period-proforma-${grp.period}`}>
                              Pro-forma PDF
                            </Button>
                            )}
                            {can("itemised_pdf") && (
                            <Button variant="outline" size="sm" className="h-7 text-xs"
                                    title="Pro-forma plus a page per deal listing its products and tracked items"
                                    onClick={() => window.open(
                                      `${api.defaults.baseURL}/commissions/partners/${encodeURIComponent(key)}/proforma.pdf?period_label=${grp.period}&itemised=true`,
                                      "_blank"
                                    )}
                                    data-testid={`period-proforma-itemised-${grp.period}`}>
                              Itemised PDF
                            </Button>
                            )}
                            {can("email") && (
                            <Button variant="outline" size="sm" className="h-7 text-xs text-sky-700 border-sky-200 hover:bg-sky-50"
                                    onClick={() => setEmailDialog({
                                      period: grp.period,
                                      defaultTo: data?.org?.contact_email || "",
                                    })}
                                    data-testid={`period-email-proforma-${grp.period}`}>
                              <Mail className="h-3.5 w-3.5 mr-1" /> Email
                            </Button>
                            )}
                            {can("accept") && (
                            <Button variant="outline" size="sm" className="h-7 text-xs text-violet-700 border-violet-200 hover:bg-violet-50"
                                    onClick={() => setReissue({ period: grp.period })}
                                    title="Re-issue a corrected pro forma for this month"
                                    data-testid={`period-reissue-${grp.period}`}>
                              <Undo2 className="h-3.5 w-3.5 mr-1 rotate-180" /> Re-issue
                            </Button>
                            )}
                            {!can("accept") ? null : acceptance ? (
                              <Button variant="outline" size="sm"
                                      className="h-7 text-xs text-slate-600 border-slate-300 hover:bg-slate-50"
                                      disabled={acceptingPeriod === grp.period}
                                      onClick={() => revokeAcceptance(grp.period)}
                                      data-testid={`period-revoke-accept-${grp.period}`}>
                                <Undo2 className="h-3.5 w-3.5 mr-1" /> Revoke accept
                              </Button>
                            ) : (
                              <Button variant="outline" size="sm"
                                      className="h-7 text-xs text-violet-700 border-violet-200 hover:bg-violet-50"
                                      disabled={acceptingPeriod === grp.period}
                                      onClick={() => acceptProforma(grp.period)}
                                      data-testid={`period-accept-${grp.period}`}>
                                <ShieldCheck className="h-3.5 w-3.5 mr-1" /> Accept
                              </Button>
                            )}
                          </div>
                        )}
                        {!isStaff && !acceptance && (
                          <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                            <Button variant="outline" size="sm"
                                    className="h-7 text-xs text-violet-700 border-violet-200 hover:bg-violet-50"
                                    disabled={acceptingPeriod === grp.period}
                                    onClick={() => acceptProforma(grp.period)}
                                    data-testid={`period-accept-${grp.period}`}>
                              <ShieldCheck className="h-3.5 w-3.5 mr-1" /> Accept pro-forma
                            </Button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
                if (isCollapsed) return [header];
                const paymentRows = grpPayments.map((p) => (
                  <tr key={p.payment_id} className="bg-sky-50/40"
                      data-testid={`payment-row-${p.payment_id}`}>
                    <td colSpan={colSpan} className="px-3 py-1.5">
                      <div className="flex items-center gap-3 text-xs">
                        <span className="rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-medium text-sky-800">Payment</span>
                        <span className="font-medium text-slate-700 tabular-nums">{money(p.amount)}</span>
                        {p.method && <span className="text-slate-500">via {p.method}</span>}
                        {p.reference && <span className="text-slate-400">ref {p.reference}</span>}
                        {p.paid_at && <span className="text-slate-400">{(p.paid_at || "").slice(0, 10)}</span>}
                        {p.notes && <span className="text-slate-500 truncate max-w-md" title={p.notes}>“{p.notes}”</span>}
                        {p.recorded_by_name && <span className="text-slate-400">by {p.recorded_by_name}</span>}
                        {can("payments") && (
                          <span className="ml-auto flex gap-1">
                            <button className="text-slate-400 hover:text-slate-700 p-1"
                                    onClick={() => setPaymentDialog({ period: p.period_label, editing: p })}
                                    data-testid={`payment-edit-${p.payment_id}`}>
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                            <button className="text-rose-400 hover:text-rose-700 p-1"
                                    onClick={() => deletePayment(p)}
                                    data-testid={`payment-delete-${p.payment_id}`}>
                              <XIcon className="h-3.5 w-3.5" />
                            </button>
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ));
                const rowsEls = grp.deals.map((d) => {
                  const ov = d.override || {};
                  const revenue    = ov.total       ?? d.total;
                  const commission = ov.commission  ?? d.commission;
                  const level      = ov.level       ?? d.level_name;
                  const overridden = !!(ov.total !== undefined || ov.commission !== undefined || ov.level);
                  if (editingDeal === d.deal_id) {
                    return <DealEditRow key={d.deal_id} deal={d} saving={savingDeal}
                                        onCancel={() => setEditingDeal(null)}
                                        onSave={(patch) => saveDeal(d, patch)} />;
                  }
                  return (
                    <Fragment key={d.deal_id}>
                    <tr data-testid={`partner-deal-row-${d.deal_id}`} className="hover:bg-slate-50/50">
                      <td className="px-3 py-2 text-xs text-slate-400">{d.period_label}</td>
                      <td className="px-3 py-2 text-xs text-slate-600">
                        <button type="button" onClick={() => setExpandedDeal(expandedDeal === d.deal_id ? null : d.deal_id)}
                                className="inline-flex items-center gap-1 text-left hover:text-slate-900"
                                title={expandedDeal === d.deal_id ? "Hide products and tracked items" : "Show products and tracked items"}
                                data-testid={`deal-expand-${d.deal_id}`}>
                          {expandedDeal === d.deal_id
                            ? <ChevronDown className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                            : <ChevronRight className="h-3.5 w-3.5 shrink-0 text-slate-400" />}
                          <span className="underline-offset-2 hover:underline">{decodeHtml(d.domain)}</span>
                          {d.is_new && <NewPill kind="deal" testId={`partner-deal-new-${d.deal_id}`} />}
                          {d.line_count ? <span className="text-[10px] text-slate-400">· {d.line_count} item{d.line_count === 1 ? "" : "s"}</span> : null}
                        </button>
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-500">{decodeHtml(d.deal_name)}</td>
                      <td className="px-3 py-2 text-xs">{level}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{money(revenue)}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-medium text-emerald-700">
                        {money(commission)}
                        {overridden && <span className="ml-1 text-[9px] text-amber-600 font-normal">(override)</span>}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {can("release_hold") ? (
                          <Switch checked={!!d.payable} onCheckedChange={() => togglePayable(d)}
                                  data-testid={`deal-payable-toggle-${d.deal_id}`} />
                        ) : (
                          <span className={`inline-flex rounded px-1.5 py-0.5 text-[10px] font-medium
                                            ${d.payable ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                            {d.payable ? "Payable" : "Held"}
                          </span>
                        )}
                      </td>
                      {showActions && (
                        <td className="px-3 py-2 text-right">
                          <button onClick={() => setEditingDeal(d.deal_id)}
                                  data-testid={`deal-edit-${d.deal_id}`}
                                  className="text-slate-400 hover:text-slate-700 p-1">
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                        </td>
                      )}
                    </tr>
                    {expandedDeal === d.deal_id && (
                      <tr className="bg-slate-50/70" data-testid={`deal-detail-row-${d.deal_id}`}>
                        <td colSpan={colSpan} className="p-0 border-l-2 border-emerald-300">
                          <DealLinesPanel dealId={d.deal_id} />
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  );
                });
                return [header, ...paymentRows, ...rowsEls];
              }) : rows.map((r) => (
                <tr key={r.row_id} className="hover:bg-slate-50/50">
                  <td className="px-3 py-2 text-xs">{r.period_label}</td>
                  <td className="px-3 py-2 text-xs">{r.level_name}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.deals}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(r.total)}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-medium text-emerald-700">{money(r.commission)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-xs text-slate-500">
                    {(r.standard_commission_rate || 0).toFixed(2)}%
                  </td>
                </tr>
              ))}
              {deals.length === 0 && rows.length === 0 && (
                <tr>
                  <td colSpan={showActions ? 8 : 7} className="px-3 py-10 text-center text-sm text-slate-400"
                      data-testid="partner-commissions-empty">
                    No commission history for this partner yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
      {paymentDialog && (
        <PaymentDialog
          period={paymentDialog.period}
          editing={paymentDialog.editing}
          suggestedAmount={paymentDialog.suggestedAmount}
          saving={savingPayment}
          onClose={() => setPaymentDialog(null)}
          onSubmit={submitPayment}
        />
      )}
      {reissue && (
        <ReissueDialog partnerKey={key} period={reissue.period}
                       onClose={() => setReissue(null)} onDone={() => load({ silent: true })} />
      )}
      {emailDialog && (
        <ProformaEmailDialog
          period={emailDialog.period}
          defaultTo={emailDialog.defaultTo}
          saving={sendingEmail}
          onClose={() => setEmailDialog(null)}
          onSubmit={sendProformaEmail}
        />
      )}
      {reconcileDialog && (
        <ReconcileDialog
          payment={reconcileDialog.payment}
          availablePeriods={timeseries.map((t) => t.period_label).filter(Boolean)}
          saving={reconciling}
          onClose={() => setReconcileDialog(null)}
          onSubmit={runReconcile}
        />
      )}
      {data?.org?.org_id && (
        <PartnerEditDialog
          orgId={data.org.org_id}
          initialOrg={data.org}
          open={editOpen}
          onOpenChange={setEditOpen}
          onSaved={load}
        />
      )}
    </div>
  );
}

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";



function DealEditRow({ deal, saving, onSave, onCancel }) {
  const ov = deal.override || {};
  const [total,      setTotal]      = useState(String(ov.total      ?? deal.total      ?? ""));
  const [commission, setCommission] = useState(String(ov.commission ?? deal.commission ?? ""));
  const [level,      setLevel]      = useState(String(ov.level      ?? deal.level_name ?? ""));
  const [note,       setNote]       = useState(String(ov.note       ?? ""));

  const submit = () => {
    onSave({
      total:      total      === "" ? null : Number(total),
      commission: commission === "" ? null : Number(commission),
      level:      level.trim() || null,
      note:       note.trim() || null,
    });
  };

  return (
    <tr className="bg-amber-50/60" data-testid={`deal-edit-row-${deal.deal_id}`}>
      <td className="px-3 py-2 text-xs">{deal.period_label}</td>
      <td className="px-3 py-2 text-xs text-slate-600">{deal.domain}</td>
      <td className="px-3 py-2 text-xs text-slate-500">{deal.deal_name}</td>
      <td className="px-3 py-2">
        <Input value={level} onChange={(e) => setLevel(e.target.value)}
               className="h-7 text-xs w-24" data-testid={`deal-edit-level-${deal.deal_id}`} />
      </td>
      <td className="px-3 py-2 text-right">
        <Input value={total} type="number" step="0.01"
               onChange={(e) => setTotal(e.target.value)}
               className="h-7 text-xs text-right w-24 ml-auto"
               data-testid={`deal-edit-total-${deal.deal_id}`} />
      </td>
      <td className="px-3 py-2 text-right">
        <Input value={commission} type="number" step="0.01"
               onChange={(e) => setCommission(e.target.value)}
               className="h-7 text-xs text-right w-24 ml-auto"
               data-testid={`deal-edit-commission-${deal.deal_id}`} />
      </td>
      <td className="px-3 py-2 text-center">
        <Input value={note} placeholder="Note"
               onChange={(e) => setNote(e.target.value)}
               className="h-7 text-xs w-32 mx-auto"
               data-testid={`deal-edit-note-${deal.deal_id}`} />
      </td>
      <td className="px-3 py-2 text-right whitespace-nowrap">
        <button onClick={submit} disabled={saving} data-testid={`deal-edit-save-${deal.deal_id}`}
                className="p-1 text-emerald-600 hover:text-emerald-800 disabled:opacity-40">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
        </button>
        <button onClick={onCancel} data-testid={`deal-edit-cancel-${deal.deal_id}`}
                className="p-1 text-slate-400 hover:text-slate-700">
          <XIcon className="h-3.5 w-3.5" />
        </button>
      </td>
    </tr>
  );
}


function ProformaEmailDialog({ period, defaultTo, saving, onClose, onSubmit }) {
  const [form, setForm] = useState(() => ({
    period, to: defaultTo || "", cc: "", note: "", test_send: false, itemised: false,
  }));
  const submit = () => {
    if (!form.test_send && !form.to.trim()) return;
    onSubmit(form);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="proforma-email-dialog">
        <DialogHeader>
          <DialogTitle>Email pro-forma — {period}</DialogTitle>
          <DialogDescription>
            Sends the pro-forma PDF as an email attachment via Resend.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <label className={`flex items-start gap-2 rounded border p-2.5 cursor-pointer ${form.itemised ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-slate-50"}`}
                 data-testid="proforma-email-itemised-row">
            <input type="checkbox" checked={form.itemised}
                   onChange={(e) => setForm({ ...form, itemised: e.target.checked })}
                   className="mt-1" data-testid="proforma-email-itemised" />
            <div className="text-xs">
              <div className="font-medium text-slate-800">Attach itemised detail</div>
              <div className="text-slate-500 mt-0.5">
                Adds a page per deal listing its products (15 × call path channels…) and every tracked item (100@domain…) with billed amount, rate and commission.
              </div>
            </div>
          </label>
          <label className={`flex items-start gap-2 rounded border p-2.5 cursor-pointer ${form.test_send ? "border-sky-300 bg-sky-50" : "border-slate-200 bg-slate-50"}`}
                 data-testid="proforma-email-test-row">
            <input type="checkbox" checked={form.test_send}
                   onChange={(e) => setForm({ ...form, test_send: e.target.checked })}
                   className="mt-1" data-testid="proforma-email-test-send" />
            <div className="text-xs">
              <div className="font-medium text-slate-800">Send preview to me only</div>
              <div className="text-slate-500 mt-0.5">
                Redirects the email to your own address and bypasses sandbox once so you can preview the real formatting. Subject is tagged <b>[TEST]</b>. Doesn&rsquo;t count as &ldquo;sent to partner&rdquo; for cooldowns or last-emailed pills.
              </div>
            </div>
          </label>
          <div>
            <Label className="text-xs">To (comma-separated)</Label>
            <Input value={form.to}
                   onChange={(e) => setForm({ ...form, to: e.target.value })}
                   placeholder="finance@partner.com"
                   disabled={form.test_send}
                   data-testid="proforma-email-to" />
            {form.test_send ? (
              <div className="mt-1 text-[10px] text-sky-700">
                Ignored — the email will go to your account&rsquo;s address.
              </div>
            ) : !defaultTo && (
              <div className="mt-1 text-[10px] text-amber-600">
                No contact email on file for this partner — enter at least one recipient.
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs">CC (optional)</Label>
            <Input value={form.cc}
                   onChange={(e) => setForm({ ...form, cc: e.target.value })}
                   placeholder="psm@unitedcloud.ca"
                   disabled={form.test_send}
                   data-testid="proforma-email-cc" />
          </div>
          <div>
            <Label className="text-xs">Message (optional)</Label>
            <Textarea rows={3} value={form.note}
                      onChange={(e) => setForm({ ...form, note: e.target.value })}
                      placeholder="Hi — attached is your pro-forma for review…"
                      data-testid="proforma-email-note" />
          </div>
          <div className="text-[11px] text-slate-500">
            The PDF is attached automatically. The partner can reply or click <b>Accept</b>
            in the portal to confirm the amounts.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="proforma-email-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving || (!form.test_send && !form.to.trim())}
                  className={form.test_send
                    ? "bg-sky-600 hover:bg-sky-700 text-white"
                    : "bg-primary hover:bg-primary/90 text-primary-foreground"}
                  data-testid="proforma-email-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (form.test_send ? "Send test to me" : "Send")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ReconcileDialog({ payment, availablePeriods, saving, onClose, onSubmit }) {
  const [form, setForm] = useState({
    payment_id:    payment.payment_id,
    target_period: availablePeriods[availablePeriods.length - 1] || "",
    note:          "",
  });
  const submit = () => {
    if (!form.target_period) return;
    onSubmit(form);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="reconcile-dialog">
        <DialogHeader>
          <DialogTitle>Apply balance-forward payment</DialogTitle>
          <DialogDescription>
            Reassign this historical payment onto a real monthly period to zero out that period&rsquo;s owing balance.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-md border border-slate-200 bg-slate-50 p-2.5 text-xs text-slate-600">
            <div>Payment <b className="tabular-nums">{money(payment.amount)}</b>
                 {payment.method && <> · via <b>{payment.method}</b></>}
                 {payment.reference && <> · ref <b>{payment.reference}</b></>}</div>
            {payment.paid_at && <div className="text-slate-400 mt-0.5">Paid on {(payment.paid_at || "").slice(0,10)}</div>}
            {payment.notes && <div className="mt-1 italic text-slate-500">“{payment.notes}”</div>}
          </div>
          <div>
            <Label className="text-xs">Apply to period</Label>
            <select value={form.target_period}
                    onChange={(e) => setForm({ ...form, target_period: e.target.value })}
                    className="mt-1 w-full h-9 rounded-md border border-slate-200 bg-white px-2 text-sm"
                    data-testid="reconcile-target-period">
              <option value="">Choose a period…</option>
              {availablePeriods.filter((p) => p !== "balance_forward").map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-xs">Note (optional)</Label>
            <Textarea rows={2} value={form.note}
                      onChange={(e) => setForm({ ...form, note: e.target.value })}
                      placeholder="Why this was reconciled here…"
                      data-testid="reconcile-note" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="reconcile-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving || !form.target_period}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground"
                  data-testid="reconcile-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
